import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useRepairInvoice } from '../../hooks/useRepairInvoices';
import { repairInvoicesService, RepairInvoiceFormData, RepairInvoiceItemFormData } from '../../services/repairInvoicesService';
import { formatCurrency } from '../../utils/currency';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Textarea from '../../components/ui/Textarea';
import RepairOrderSelectionModal from '../../components/ui/RepairOrderSelectionModal';

const RepairInvoiceForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { invoice, loading: invoiceLoading } = useRepairInvoice(id || null);

  const [repairInvoiceNumber, setRepairInvoiceNumber] = useState('');
  const [repairOrderId, setRepairOrderId] = useState('');
  const [selectedRepairOrder, setSelectedRepairOrder] = useState<any>(null);
  const [invoiceDate, setInvoiceDate] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled'>('draft');
  const [items, setItems] = useState<RepairInvoiceItemFormData[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showOrderSelection, setShowOrderSelection] = useState(false);

  // Initialize form with existing invoice data or defaults
  useEffect(() => {
    if (invoice) {
      setRepairInvoiceNumber(invoice.repair_invoice_number);
      setRepairOrderId(invoice.repair_order_id);
      setSelectedRepairOrder(invoice.repair_order);
      setInvoiceDate(invoice.invoice_date);
      setDescription(invoice.description || '');
      setStatus(invoice.status);
      setItems(invoice.items.map((item: any) => ({
        description: item.description,
        quantity: item.quantity,
        unit_price: item.unit_price,
        tax_rate: item.tax_rate,
      })));
    } else {
      // Set defaults for new invoice
      const today = new Date().toISOString().split('T')[0];
      setInvoiceDate(today);
      setRepairInvoiceNumber('RI-23483294'); // Default as requested
    }
  }, [invoice]);

  // Add a new item to the repair invoice
  const addItem = () => {
    setItems([
      ...items,
      {
        description: '',
        quantity: 1,
        unit_price: 0,
        tax_rate: 0,
      }
    ]);
  };

  // Update an item in the repair invoice
  const updateItem = (index: number, field: string, value: any) => {
    const newItems = [...items];
    newItems[index] = {
      ...newItems[index],
      [field]: value,
    };
    setItems(newItems);
  };

  // Remove an item from the repair invoice
  const removeItem = (index: number) => {
    const newItems = [...items];
    newItems.splice(index, 1);
    setItems(newItems);
  };

  // Calculate item subtotal
  const calculateItemSubtotal = (item: RepairInvoiceItemFormData) => {
    return item.quantity * item.unit_price;
  };

  // Calculate item tax amount
  const calculateItemTax = (item: RepairInvoiceItemFormData) => {
    return calculateItemSubtotal(item) * (item.tax_rate / 100);
  };

  // Calculate total amount
  const calculateTotal = () => {
    return items.reduce((sum, item) => {
      const subtotal = calculateItemSubtotal(item);
      const tax = calculateItemTax(item);
      return sum + subtotal + tax;
    }, 0);
  };

  // Handle repair order selection
  const handleRepairOrderSelect = (order: any) => {
    setRepairOrderId(order.id);
    setSelectedRepairOrder(order);
    setShowOrderSelection(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!repairOrderId) {
      setError('Please select a repair order');
      return;
    }

    if (items.length === 0) {
      setError('Please add at least one item to the invoice');
      return;
    }

    // Validate all items have required fields
    for (const item of items) {
      if (!item.description.trim()) {
        setError('All items must have a description');
        return;
      }
      if (item.quantity <= 0) {
        setError('All items must have a quantity greater than 0');
        return;
      }
      if (item.unit_price < 0) {
        setError('Item prices cannot be negative');
        return;
      }
    }

    try {
      setError('');
      setLoading(true);

      const invoiceData: RepairInvoiceFormData = {
        repair_invoice_number: repairInvoiceNumber,
        repair_order_id: repairOrderId,
        invoice_date: invoiceDate,
        description: description.trim() || undefined,
        status,
        items,
      };

      let result;

      if (id) {
        // Update existing repair invoice
        result = await repairInvoicesService.updateRepairInvoice(id, invoiceData);
      } else {
        // Create new repair invoice
        result = await repairInvoicesService.createRepairInvoice(invoiceData);
      }

      if (result.error) {
        setError(result.error.message);
      } else {
        navigate('/repair-invoices');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save repair invoice');
    }

    setLoading(false);
  };

  if (invoiceLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {id ? 'Edit Repair Invoice' : 'Create Repair Invoice'}
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2">
          <div className="card">
            <form onSubmit={handleSubmit} className="px-4 py-5 sm:p-6">
              {error && (
                <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
                  {error}
                </div>
              )}

              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 mb-6">
                <Input
                  label="Repair Invoice Number"
                  type="text"
                  value={repairInvoiceNumber}
                  onChange={(e) => setRepairInvoiceNumber(e.target.value)}
                  required
                />

                <Input
                  label="Invoice Date"
                  type="date"
                  value={invoiceDate}
                  onChange={(e) => setInvoiceDate(e.target.value)}
                  required
                />

                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Repair Order
                  </label>
                  {selectedRepairOrder ? (
                    <div className="flex items-center justify-between p-3 border border-gray-300 rounded-md bg-gray-50">
                      <div>
                        <div className="font-medium">{selectedRepairOrder.repair_order_number}</div>
                        <div className="text-sm text-gray-600">
                          {selectedRepairOrder.customer?.name} - {selectedRepairOrder.device_brand} {selectedRepairOrder.device_model}
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setShowOrderSelection(true)}
                      >
                        Change
                      </Button>
                    </div>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowOrderSelection(true)}
                      className="w-full"
                    >
                      Select Repair Order
                    </Button>
                  )}
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <Textarea
                    value={description}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDescription(e.target.value)}
                    rows={3}
                    placeholder="Optional description for the repair invoice"
                  />
                </div>
              </div>

              {/* Invoice Items */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Invoice Items</h3>
                  <Button type="button" variant="secondary" onClick={addItem}>
                    Add Item
                  </Button>
                </div>

                {items.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No items added yet. Click "Add Item" to add items to the invoice.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead>
                        <tr>
                          <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Description
                          </th>
                          <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Quantity
                          </th>
                          <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Unit Price
                          </th>
                          <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Tax %
                          </th>
                          <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Subtotal
                          </th>
                          <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {items.map((item, index) => (
                          <tr key={index}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <input
                                type="text"
                                className="input-field"
                                value={item.description}
                                onChange={(e) => updateItem(index, 'description', e.target.value)}
                                placeholder="Item description"
                                required
                              />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <input
                                type="number"
                                className="input-field"
                                value={item.quantity}
                                onChange={(e) => updateItem(index, 'quantity', Number(e.target.value))}
                                min="1"
                                step="1"
                                required
                              />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <input
                                type="number"
                                className="input-field"
                                value={item.unit_price}
                                onChange={(e) => updateItem(index, 'unit_price', Number(e.target.value))}
                                min="0"
                                step="0.01"
                                required
                              />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <input
                                type="number"
                                className="input-field"
                                value={item.tax_rate}
                                onChange={(e) => updateItem(index, 'tax_rate', Number(e.target.value))}
                                min="0"
                                max="100"
                                step="0.01"
                              />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap font-medium">
                              {formatCurrency(calculateItemSubtotal(item) + calculateItemTax(item))}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <button
                                type="button"
                                onClick={() => removeItem(index)}
                                className="text-red-600 hover:text-red-900"
                              >
                                Remove
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-gray-50">
                        <tr>
                          <td colSpan={4} className="px-6 py-3 text-right text-sm font-medium text-gray-900">
                            Total Amount
                          </td>
                          <td className="px-6 py-3 text-sm font-medium text-gray-900">
                            {formatCurrency(calculateTotal())}
                          </td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/repair-invoices')}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Save Repair Invoice'}
                </Button>
              </div>
            </form>
          </div>
        </div>

        {/* Repair Order Details Sidebar */}
        {selectedRepairOrder && (
          <div className="card">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Repair Order Details</h3>
            </div>
            <div className="px-4 py-5 sm:p-6">
              <div className="space-y-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Order Number</dt>
                  <dd className="mt-1 text-sm text-gray-900">{selectedRepairOrder.repair_order_number}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Device</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {selectedRepairOrder.device_brand} {selectedRepairOrder.device_model}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Serial Number</dt>
                  <dd className="mt-1 text-sm text-gray-900">{selectedRepairOrder.device_serial}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Problem</dt>
                  <dd className="mt-1 text-sm text-gray-900">{selectedRepairOrder.device_problem}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Status</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    <span className={`badge ${repairInvoicesService.getStatusBadgeClass(selectedRepairOrder.status)}`}>
                      {selectedRepairOrder.status}
                    </span>
                  </dd>
                </div>
              </div>

              <div className="mt-6 border-t border-gray-200 pt-6">
                <h4 className="text-md font-medium text-gray-900 mb-4">Customer Information</h4>
                <div className="space-y-3">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Name</dt>
                    <dd className="mt-1 text-sm text-gray-900">{selectedRepairOrder.customer?.name || 'N/A'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Email</dt>
                    <dd className="mt-1 text-sm text-gray-900">{selectedRepairOrder.customer?.email || 'N/A'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Phone</dt>
                    <dd className="mt-1 text-sm text-gray-900">{selectedRepairOrder.customer?.phone || 'N/A'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Balance</dt>
                    <dd className="mt-1 text-sm font-medium text-gray-900">
                      {formatCurrency(selectedRepairOrder.customer?.balance || 0)}
                    </dd>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Repair Order Selection Modal */}
      {showOrderSelection && (
        <RepairOrderSelectionModal
          onSelect={handleRepairOrderSelect}
          onClose={() => setShowOrderSelection(false)}
        />
      )}
    </div>
  );
};

export default RepairInvoiceForm;